import { DynamicFormsApiService } from '@api_patient/dynamic_forms_api.service';
import { DateService } from '@application/time/date.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { FormManager } from '@feature/dynamic_form/form_manager/mod';
import { useTimeSlots } from '@feature/time_slot/use_time_slots';
import type { AdditionalParticipantApiInput, FormApiModel } from '@visiba/api-vcs-client-v2';
import { FormOpportunity, States } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import type { Dayjs } from 'dayjs';
import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';
import type { RespondentType } from '../screens/screen_who_is_respondant/use_who_is_respondant';
import { useBasketDataStorage } from './use_basket_data_storage';

interface BookingBasketSpec {
	useReasonForVisits: boolean;
}

class TimeSlotLockUpdateError extends Error {
	constructor(
		message: string,
		readonly errorCode: string | null,
	) {
		super(message);
	}
}

function isApiErrorEnvelope(error: unknown): error is { ErrorCode: string | null; ErrorMessage: string | null } {
	return (
		typeof error === 'object' &&
		error !== null &&
		'ErrorCode' in error &&
		(typeof error.ErrorCode === 'string' || error.ErrorCode === null) &&
		'ErrorMessage' in error &&
		(typeof error.ErrorMessage === 'string' || error.ErrorMessage === null)
	);
}

export interface BookingBasketHook {
	// Forms are stored in a ref-backed getter to keep parity with the old service API.
	basketForms: FormApiModel[];
	basketReady: boolean;
	readonly formManager: FormManager;
	readonly dateService: DateService;
	loadBasket(spec: BookingBasketSpec): void;
	restoreBasket(): AsyncResult<void>;
	clearBasket(): void;
	getBasket(): BookingBasket;
	setBasket(newUserData: Partial<BasketStorageData>): void;
	unlockTimeSlot(): AsyncResult<void>;
	updateLockedTimeSlot(timeSlotLockId: string): AsyncResult<void>;
	lockTimeSlot(timeSlotId: string): AsyncResult<void>;
	commitBasket(data: CommitSpec): AsyncResult<CommitResult>;
}

const BookingBasketContext = createContext<BookingBasketHook | null>(null);

export function useBookingBasket(): BookingBasketHook {
	const context = useContext(BookingBasketContext);
	if (context == null) {
		throw new Error('useBookingBasket must be used within a <BookingBasketProvider>');
	}
	return context;
}

export function BookingBasketProvider({ children }: PropsWithChildren) {
	const universalConfig = useResolve(UniversalConfig);
	const unitService = useResolve(UnitService);
	const dynamicFormsApiService = useResolve(DynamicFormsApiService);
	const timeSlotsApi = useTimeSlots();
	const formManager = useResolve(FormManager);
	const dateService = useResolve(DateService);
	const unit = unitService.getUnitNodeInTree(universalConfig.selectedUnitId);
	const receptionId = unit.receptionId;
	const basketDataStorage = useBasketDataStorage({ receptionId });
	const loggerRef = useRef(new Logger('useBookingBasket'));
	const formsRef = useRef<FormApiModel[]>([]);
	const useReasonForVisitsRef = useRef(false);
	const [basketReady, setBasketReady] = useState(false);

	const getBasket = useCallback((): BookingBasket => {
		const data = basketDataStorage.getBasketDataStorage();
		let currentDate: Dayjs | null = null;

		if (data.currentDate && dateService.isValidDate(data.currentDate)) {
			currentDate = dateService.newDate(data.currentDate, 'YYYY-MM-DD');
		}

		return {
			...data,
			currentDate,
		};
	}, [basketDataStorage.getBasketDataStorage, dateService]);

	const setBasket = useCallback(
		(newUserData: Partial<BasketStorageData>) => {
			basketDataStorage.setBasketDataStorage({ ...newUserData });
		},
		[basketDataStorage.setBasketDataStorage],
	);

	const unlockTimeSlot = useCallback(async (): AsyncResult<void> => {
		const { timeSlotLockId } = getBasket();
		if (!timeSlotLockId) return Ok();

		try {
			await timeSlotsApi.deleteLockTimeSlot(universalConfig.selectedUnitId, timeSlotLockId);
			await setBasket({ timeSlotLockId: null });
			return Ok();
		} catch (error) {
			return Err(error instanceof Error ? error : new Error(String(error)));
		}
	}, [getBasket, setBasket, timeSlotsApi, universalConfig.selectedUnitId]);

	const populateForms = useCallback(async (): AsyncResult<void> => {
		if (!formsRef.current.length) {
			const formsResult = await dynamicFormsApiService.getAllForms(
				universalConfig.selectedUnitId,
				getBasket().reasonForVisit,
				FormOpportunity.BeforeBooking,
			);
			if (formsResult.err) return Err(formsResult.err);
			formsRef.current = formsResult.ok.data;
		}

		if (formsRef.current.length) {
			let formRespondantId = getBasket().formRespondantId;
			if (!formRespondantId) {
				formRespondantId = v4();
				await setBasket({ formRespondantId });
			}

			formManager.addRespondants(
				formsRef.current
					.filter((form) => !formManager.getFormByFormId(form.Id))
					.map((form) => ({
						completed: false,
						editable: true,
						formId: form.Id,
						header: form.Header ?? '',
						opportunity: FormOpportunity.BeforeBooking,
						issueTrackingId: '',
						dataProvider: { type: 'local', id: formRespondantId ?? '' },
					})) ?? [],
			);
		}

		return Ok();
	}, [dynamicFormsApiService, universalConfig.selectedUnitId, getBasket, setBasket, formManager]);

	const clearBasket = useCallback((): void => {
		basketDataStorage.clearBasketDataStorage();
		setBasketReady(true);
	}, [basketDataStorage.clearBasketDataStorage]);

	const updateLockedTimeSlot = useCallback(
		async (timeSlotLockId: string): AsyncResult<void> => {
			try {
				const updatedLock = await timeSlotsApi.updateLockedTimeSlot(universalConfig.selectedUnitId, timeSlotLockId);
				await setBasket({ timeSlotLockId: updatedLock.Id });
				return Ok();
			} catch (error) {
				if (isApiErrorEnvelope(error)) {
					return Err(new TimeSlotLockUpdateError(error.ErrorMessage ?? 'Failed to update time slot lock', error.ErrorCode));
				}
				return Err(error instanceof Error ? error : new Error(String(error)));
			}
		},
		[timeSlotsApi, universalConfig.selectedUnitId, setBasket],
	);

	const lockTimeSlot = useCallback(
		async (timeSlotId: string): AsyncResult<void> => {
			await unlockTimeSlot();
			try {
				const lock = await timeSlotsApi.lockTimeSlot(universalConfig.selectedUnitId, timeSlotId);
				await setBasket({ timeSlotLockId: lock.Id });
				return Ok();
			} catch (error) {
				return Err(error instanceof Error ? error : new Error(String(error)));
			}
		},
		[unlockTimeSlot, timeSlotsApi, universalConfig.selectedUnitId, setBasket],
	);

	const restoreBasket = useCallback(async (): AsyncResult<void> => {
		const basket = getBasket();

		if (basket.rescheduleTicket) {
			formsRef.current = [];
			formManager.removeForms();
			setBasketReady(true);
			return Ok();
		}

		if (basket.timeSlotId && (!useReasonForVisitsRef.current || basket.reasonForVisit !== null)) {
			const populateFormsResult = await populateForms();
			if (populateFormsResult.err) {
				setBasketReady(true);
				return Err(populateFormsResult.err);
			}
		} else {
			formsRef.current = [];
			formManager.removeForms();
		}

		// Claim the anonymous lock after login, or replace it if it is no longer available.
		const { timeSlotLockId, timeSlotId } = getBasket();
		if (timeSlotLockId && timeSlotId) {
			const claimResult = await updateLockedTimeSlot(timeSlotLockId);
			if (claimResult.err) {
				loggerRef.current.logError(`Failed to claim restored time slot lock: ${claimResult.err.message}`);
				if (claimResult.err instanceof TimeSlotLockUpdateError && claimResult.err.errorCode === 'NotFound_TimeslotLock') {
					const replacementLockResult = await lockTimeSlot(timeSlotId);
					if (replacementLockResult.err) {
						setBasket({ timeSlotLockId: null, timeSlotId: null });
					}
				} else {
					setBasketReady(true);
					return Err(claimResult.err);
				}
			}
		}

		setBasketReady(true);
		return Ok();
	}, [getBasket, formManager, populateForms, updateLockedTimeSlot, lockTimeSlot, setBasket]);

	const validate = useCallback((): Result<void> => {
		if (useReasonForVisitsRef.current && getBasket().reasonForVisit == null) {
			return Err(new Error('Reason for visit is required'));
		}

		if (formsRef.current.length && formManager.getForms().find((form) => !form.getState().completed)) {
			return Err(new Error('UnansweredFormsError'));
		}

		return Ok();
	}, [getBasket, formManager]);

	const commitBasket = useCallback(
		async (data: CommitSpec): AsyncResult<CommitResult> => {
			const validateResult = validate();
			if (validateResult.err) return Err(validateResult.err);

			let bookingResult;
			try {
				bookingResult = await timeSlotsApi.bookTimeSlot(universalConfig.selectedUnitId, getBasket().timeSlotId ?? '', {
					AsAgentFor: getBasket().asAgentFor ?? null,
					AsAgentForName: null,
					ConditionId: getBasket().reasonForVisit,
					ExternalData: [],
					Files: data.files ?? [],
					Reason: data.reason,
					RedRobinSessionId: data.redRobinSessionId ?? null,
					TimeslotLockId: null,
					AdditionalParticipants: getBasket().additionalParticipants ?? [],
				});
			} catch (error) {
				return Err(error instanceof Error ? error : new Error(String(error)));
			}

			if (bookingResult.Forms.length) {
				const forms = formManager.getForms();
				for (const form of bookingResult.Forms) {
					const formWorker = forms.find((f) => f.formId === form.FormId);
					if (formWorker) {
						const answersResult = await formWorker.formDataProvider.getAnswers();
						if (answersResult.err) return Err(answersResult.err);

						const postAnswersResult = await dynamicFormsApiService.answer(
							universalConfig.selectedUnitId,
							form.RespondantId,
							answersResult.ok.answers,
						);
						if (postAnswersResult.err) return Err(postAnswersResult.err);

						const setCompletionResult = await dynamicFormsApiService.setCompletion(universalConfig.selectedUnitId, form.RespondantId);
						if (setCompletionResult.err) return Err(setCompletionResult.err);
					}
				}
			}

			let payment = null;
			if (bookingResult.Payment && bookingResult.Payment.State === States.Unpaid && bookingResult.Payment.BillId) {
				payment = {
					billId: bookingResult.Payment.BillId,
					amount: `${bookingResult.Price?.Formatted}`,
				};
			}

			return Ok({ payment, ticket: bookingResult.Ticket });
		},
		[validate, timeSlotsApi, universalConfig.selectedUnitId, getBasket, formManager, dynamicFormsApiService],
	);

	const value = useMemo<BookingBasketHook>(() => {
		const hook: BookingBasketHook = {
			get basketForms() {
				return formsRef.current;
			},
			basketReady,
			formManager,
			dateService,
			loadBasket(spec) {
				useReasonForVisitsRef.current = spec.useReasonForVisits;
			},
			restoreBasket,
			clearBasket,
			getBasket,
			setBasket,
			unlockTimeSlot,
			updateLockedTimeSlot,
			lockTimeSlot,
			commitBasket,
		};
		return hook;
	}, [
		basketReady,
		formManager,
		dateService,
		restoreBasket,
		clearBasket,
		getBasket,
		setBasket,
		unlockTimeSlot,
		updateLockedTimeSlot,
		lockTimeSlot,
		commitBasket,
	]);

	return <BookingBasketContext.Provider value={value}>{children}</BookingBasketContext.Provider>;
}

interface CommitSpec {
	reason: string;
	files?: string[];
	redRobinSessionId?: string;
}

export interface CommitResult {
	payment: {
		billId: number;
		amount: string;
	} | null;
	ticket: string;
}

interface BookingBasketCommon {
	respondantType: Nullable<RespondentType>;
	asAgentFor: Nullable<string>;
	reasonForVisit: Nullable<number>;
	timeSlotId: Nullable<string>;
	timeSlotLockId: Nullable<string>;
	timeSlotFilterResourceId: Nullable<number>;
	timeSlotFilterSpecialityId: Nullable<number>;
	timeSlotFilterUnitId: Nullable<number>;
	formRespondantId: Nullable<Guid>;
	rescheduleTicket: Nullable<string>;
	reschedulingReason: Nullable<string>;
	additionalParticipants: Nullable<AdditionalParticipantApiInput[]>;
}

export interface BookingBasket extends BookingBasketCommon {
	currentDate: Nullable<Dayjs>;
}

export interface BasketStorageData extends BookingBasketCommon {
	currentDate: Nullable<string>;
}
